import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";
// import { useDebounce } from "src/hooks/useDebonce";
import { textTruncate } from "src/helpers/truncateText";
import { useSearchParams } from "react-router-dom";
import { Tooltip } from "@chakra-ui/react";
import { ReactComponent as MonitoredIcon } from "src/assets/images/monitored_circle.svg";

export const MonitoringContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = {
  LOW: "#05CD99",
  MEDIUM: "#F8961E",
  HIGH: "#F94144",
};

const MonitoringProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const [searchParam, setSearchParam] = useState("");
  const [searchValue, setSearchValue] = useState("");
  // const debouncedValue = useDebounce<string>(searchParam, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const pageQuery = queryParams.get("page") || 1;
  const tabQuery = queryParams.get("tab");
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      "user_monitoring",
      searchValue,
      pageQuery,
      queryParams.toString(),
    ],
    staleTime: 30000,
    enabled: tabQuery === "single",
    queryFn: async () => {
      const url = searchValue
        ? `user_monitoring?search=${searchValue}&page_size=10&page=1`
        : `user_monitoring?page_size=10&${decodeURIComponent(
            queryParams as any,
          )}`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          first_name: string;
          last_name: string;
          other_name: string;
          source: string;
          created_at: string;
          monitoring_period: string;
          risk_status: string;
          risk_score: number;
          business_name: string;
          monitoring_changes: any;
          date_added_to_monitor: any;
        }) => ({
          id: res.id,
          "": (
            <Tooltip
              label="This profile has new monitoring status"
              hasArrow
              placement="right-end"
              isDisabled={!res.monitoring_changes}
            >
              {res.monitoring_changes ? (
                <MonitoredIcon
                  style={{ width: "15px", height: "15px", cursor: "pointer" }}
                />
              ) : (
                <></>
              )}
            </Tooltip>
          ),
          name: `${
            !res.first_name?.toLowerCase() &&
            !res.last_name?.toLowerCase() &&
            !res.other_name?.toLowerCase() &&
            !res.business_name?.toLowerCase()
              ? "N/A"
              : res.business_name &&
                  !res.first_name?.toLowerCase() &&
                  !res.last_name?.toLowerCase() &&
                  !res.other_name?.toLowerCase()
                ? textTruncate(res.business_name, 20)
                : textTruncate(
                    `${res.first_name?.toLowerCase() || ""} ${
                      res.other_name?.toLowerCase() || ""
                    } ${res.last_name?.toLowerCase() || ""}`,
                    20,
                  )
          }`,
          date: format(new Date(res.date_added_to_monitor), "dd/MM/yyyy"),
          "monitoring period": res.monitoring_period || "N/A",
          "risk intensity": (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  background: "#D9DDE3",
                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    background: score_color[res.risk_status],
                    height: "1.2vh",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    width: `${res.risk_score}%`,
                  }}
                />
              </div>
              {res.risk_score.toFixed(2)}%
            </div>
          ),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  // const handleSearch = (e: any) => {
  //   setSearchParam(e.target.value);
  // };

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
      handleSearchRecord,
      setQueryParams,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
      handleSearchRecord,
      setQueryParams,
    ],
  );
  return (
    <MonitoringContext.Provider value={contextValue}>
      {children}
    </MonitoringContext.Provider>
  );
};

export default MonitoringProvider;
